import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, CheckCircle2, XCircle, RotateCcw, Ban, UserPlus, ShieldOff } from 'lucide-react';
import WhatsAppIcon from '../common/WhatsAppIcon';
import MemorandoContent from '../processes/MemorandoContent';
import CreditProductBadge from './CreditProductBadge';
import { formatLoaLabel } from '../../utils/loaYear';
import PartnerLawyerBadge from '../common/PartnerLawyerBadge';
import QuickLawyerPartnerModal from '../lawyer-partners/QuickLawyerPartnerModal';
import { enrichedLeadService } from '../../services/enrichedLeadService';
import { lawyerPartnersService } from '../../services/lawyerPartnersService';
import { findMatchingLawyerPartners } from '../../utils/lawyerPartners';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { isLeadContactBlocked, isBadLawyerPartner } from '../../config/contactBlocklist';
import { useAuth } from '../../context/AuthContext';
import { leadStatusActionClass } from './leadStatusActionClass';

function getDecisionMeta(processData) {
  if (!processData) return null;

  if (processData.leadAutomationFinalStatus === 'CONVERTIDO_LEAD' || processData.leadsCreatedCount > 0) {
    return {
      label: 'Lead vinculado ao processo',
      className: 'bg-purple-100 text-purple-800 border-purple-200',
    };
  }

  if (processData.leadAutomationMode === 'MANUAL_READY' || processData.leadEligible) {
    return {
      label: 'Elegível para criação manual',
      className: 'bg-blue-100 text-blue-800 border-blue-200',
    };
  }

  if (processData.leadAutomationMode === 'MANUAL_REVIEW' || processData.statusSuggestion === 'REVISAR_MANUAL') {
    return {
      label: 'Revisão manual',
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };
  }

  if (processData.leadAutomationMode === 'REJECT' || processData.statusSuggestion === 'INVIAVEL') {
    return {
      label: 'Não elegível no momento',
      className: 'bg-red-100 text-red-800 border-red-200',
    };
  }

  return {
    label: 'Monitorar / revisar',
    className: 'bg-gray-100 text-gray-700 border-gray-200',
  };
}

function getViabilidadeMeta(viabilidade) {
  if (!viabilidade) return null;
  const map = {
    ALTA: 'bg-green-100 text-green-800 border-green-200',
    MEDIA: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    BAIXA: 'bg-orange-100 text-orange-800 border-orange-200',
    INCONCLUSIVO: 'bg-slate-100 text-slate-800 border-slate-200',
    INVIAVEL: 'bg-red-100 text-red-800 border-red-200',
  };

  return {
    label:
      viabilidade === 'MEDIA'
        ? 'Média'
        : viabilidade.charAt(0) + viabilidade.slice(1).toLowerCase(),
    className: map[viabilidade] || 'bg-gray-100 text-gray-700 border-gray-200',
  };
}

function formatNormalizedAnswer(answer) {
  switch (answer) {
    case 'YES':
      return 'Sim';
    case 'NO':
      return 'Não';
    case 'PARTIAL':
      return 'Parcial';
    default:
      return 'Não identificado';
  }
}

function normalizeName(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function isLawyerContact(contact) {
  return normalizeName(contact?.tipo).includes('advogado');
}

function findMatchingPoloParte(contact, processData) {
  const poloAtivo = processData?.poloAtivo || [];
  const leadName = normalizeName(contact?.name);
  const leadDoc = (contact?.document || '').replace(/\D/g, '');

  return (
    poloAtivo.find((parte) => normalizeName(parte.nome) === leadName) ||
    poloAtivo.find(
      (parte) =>
        leadDoc &&
        parte.documentos?.some((doc) => (doc.value || '').replace(/\D/g, '') === leadDoc),
    ) ||
    null
  );
}

function extractOabFromParte(parte) {
  if (!parte?.documentos?.length) return '';
  const oabDoc = parte.documentos.find((doc) => /oab/i.test(doc.type || ''));
  return oabDoc?.value || '';
}

function buildLawyerCandidateFromContact(contact, processData) {
  const parte = findMatchingPoloParte(contact, processData);
  const phones = (contact?.phones || [])
    .map((phone) => phone.telefoneFormatado || `${phone.ddd || ''}${phone.telefone || ''}`)
    .filter(Boolean);
  const emails = (contact?.emails || [])
    .map((email) => email.email || email)
    .filter(Boolean);

  return {
    nome: contact?.name || '',
    oab: extractOabFromParte(parte),
    document: contact?.document || '',
    documentos: contact?.document ? [contact.document] : [],
    emails,
    telefones: phones,
    type: 'ADVOGADO',
  };
}

/** Nome do autor (polo ativo, nunca advogado). */
function getAuthorName(processData, contacts, fallback) {
  const poloAtivo = processData?.poloAtivo || [];
  const autor = poloAtivo.find((p) => normalizeName(p.tipo) !== 'advogado');
  if (autor?.nome) return autor.nome;
  if (poloAtivo[0]?.nome) return poloAtivo[0].nome;
  if (contacts?.[0]?.name) return contacts[0].name;
  return fallback || 'Autor não identificado';
}

/** Nome da empresa/parte do polo passivo (réu, nunca advogado). */
function getCompanyName(processData) {
  const poloPassivo = processData?.poloPassivo || [];
  const parte = poloPassivo.find((p) => normalizeName(p.tipo) !== 'advogado');
  return parte?.nome || poloPassivo[0]?.nome || null;
}

export default function LeadDetailModal({ lead, onClose, onLeadUpdated }) {
  const [activeTab, setActiveTab] = useState('processo');
  const { user } = useAuth();
  const { processData, document } = lead;

  // Contatos (todos, separadamente). Fallback para o próprio lead.
  const initialContacts =
    lead.contacts && lead.contacts.length > 0
      ? lead.contacts
      : [
          {
            leadId: lead.primaryLeadId || lead.id,
            name: lead.name,
            document: lead.document,
            tipo: processData?.poloAtivo?.[0]?.tipo,
            phones: lead.phones || [],
            emails: lead.emails || [],
            status: lead.status,
            assignedTo: lead.assignedTo,
            lostReason: lead.lostReason,
            contactBlocked: lead.contactBlocked === true,
          },
        ];

  const [contacts, setContacts] = useState(initialContacts);
  const [updatingLeadId, setUpdatingLeadId] = useState(null);
  const [lawyerPartners, setLawyerPartners] = useState([]);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [partnerCandidate, setPartnerCandidate] = useState(null);

  const loaLabel = formatLoaLabel(processData?.tipoProduto);
  const decisionMeta = getDecisionMeta(processData);
  const viabilidadeMeta = getViabilidadeMeta(processData?.viabilidade);
  const authorName = getAuthorName(processData, contacts, lead.name);
  const companyName = getCompanyName(processData);

  const parecerText = processData?.parecer;
  const questionnaireText = processData?.recommendation || processData?.recomendacao;
  const hasParecer = !!(parecerText || questionnaireText);

  const totalPhones = contacts.reduce(
    (sum, c) => sum + (c.phones ? c.phones.length : 0),
    0,
  );

  const checklistEntries = useMemo(() => {
    if (!processData?.checklistAnswers || typeof processData.checklistAnswers !== 'object') {
      return [];
    }

    return Object.values(processData.checklistAnswers).filter(Boolean);
  }, [processData]);

  useEffect(() => {
    let cancelled = false;

    async function loadLawyerPartners() {
      try {
        const data = await lawyerPartnersService.getPartners();
        if (!cancelled) setLawyerPartners(data || []);
      } catch {
        if (!cancelled) setLawyerPartners([]);
      }
    }

    loadLawyerPartners();
    return () => {
      cancelled = true;
    };
  }, []);

  function getMatchedPartnerForContact(contact) {
    if (!isLawyerContact(contact) || !lawyerPartners.length) return null;
    const candidate = buildLawyerCandidateFromContact(contact, processData);
    const matches = findMatchingLawyerPartners({
      partners: lawyerPartners,
      lawyers: [candidate],
    });
    return matches[0] || null;
  }

  function handleOpenPartnerModal(contact) {
    setPartnerCandidate(buildLawyerCandidateFromContact(contact, processData));
    setPartnerModalOpen(true);
  }

  function handlePartnerCreated(partner) {
    if (partner) {
      setLawyerPartners((prev) => {
        const withoutDuplicate = prev.filter((item) => item.id !== partner.id);
        return [partner, ...withoutDuplicate];
      });
    }
    setPartnerModalOpen(false);
    setPartnerCandidate(null);
  }

  function updateContactStatus(leadId, patch) {
    setContacts((prev) =>
      prev.map((c) => (c.leadId === leadId ? { ...c, ...patch } : c)),
    );
  }

  async function handleMarkInContact(contact) {
    const matchedPartner = getMatchedPartnerForContact(contact);
    if (isLeadContactBlocked(contact, matchedPartner)) {
      alert(
        isBadLawyerPartner(matchedPartner)
          ? `Parceiro ruim (${matchedPartner.nome}). Não é permitido entrar em contato.`
          : 'Contato bloqueado. Não é permitido entrar em contato com este lead.',
      );
      return;
    }
    setUpdatingLeadId(contact.leadId);
    try {
      const assignedTo = enrichedLeadService.resolveContactUserName(user);
      await enrichedLeadService.markInContact(contact.leadId, assignedTo);
      updateContactStatus(contact.leadId, { status: 'CONTACTED', assignedTo });
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      alert(
        `Não foi possível atualizar o status: ${
          err?.response?.data?.message || err?.message || 'erro'
        }`,
      );
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleWhatsAppClick(contact) {
    const matchedPartner = getMatchedPartnerForContact(contact);
    if (isLeadContactBlocked(contact, matchedPartner)) {
      alert(
        isBadLawyerPartner(matchedPartner)
          ? `Parceiro ruim (${matchedPartner.nome}). Não é permitido abrir WhatsApp.`
          : 'Contato bloqueado. Não é permitido abrir WhatsApp para este lead.',
      );
      return;
    }
    if (!contact?.leadId || enrichedLeadService.isContactBusy(contact.status, contact.assignedTo)) return;
    try {
      const assignedTo = enrichedLeadService.resolveContactUserName(user);
      await enrichedLeadService.markInContact(contact.leadId, assignedTo);
      updateContactStatus(contact.leadId, { status: 'CONTACTED', assignedTo });
      if (onLeadUpdated) onLeadUpdated();
    } catch {
      /* não bloqueia abertura do WhatsApp */
    }
  }

  async function handleToggleContactBlocked(contact) {
    if (!contact?.leadId) return;
    const matchedPartner = getMatchedPartnerForContact(contact);
    if (isBadLawyerPartner(matchedPartner)) {
      alert(
        `Este advogado está cadastrado como parceiro ruim (${matchedPartner.nome}). Altere a classificação/rating em Advogados Parceiros para liberar o contato.`,
      );
      return;
    }
    const next = !contact.contactBlocked;
    const actionLabel = next ? 'bloquear' : 'desbloquear';
    if (
      !confirm(
        next
          ? `Bloquear contato de ${contact.name}? WhatsApp, e-mail e agente ficarão desabilitados.`
          : `Desbloquear contato de ${contact.name}?`,
      )
    ) {
      return;
    }
    setUpdatingLeadId(contact.leadId);
    try {
      await enrichedLeadService.setContactBlocked(contact.leadId, next);
      updateContactStatus(contact.leadId, { contactBlocked: next });
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      alert(
        `Não foi possível ${actionLabel} o contato: ${
          err?.response?.data?.message || err?.message || 'erro'
        }`,
      );
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleMarkConverted(contact) {
    if (!confirm(`Marcar ${contact.name} como convertido?`)) return;
    setUpdatingLeadId(contact.leadId);
    try {
      await enrichedLeadService.markConverted(contact.leadId);
      updateContactStatus(contact.leadId, { status: 'CONVERTED' });
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleMarkLost(contact) {
    const reason = window.prompt(`Motivo da perda para ${contact.name}:`, contact.lostReason || '');
    if (reason === null) return;
    setUpdatingLeadId(contact.leadId);
    try {
      await enrichedLeadService.markLost(contact.leadId, reason);
      updateContactStatus(contact.leadId, { status: 'LOST', lostReason: reason });
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleMarkRejected(contact) {
    const reason = window.prompt(
      `Motivo da inaptidão para ${contact.name} (opcional):`,
      contact.lostReason || '',
    );
    if (reason === null) return;
    setUpdatingLeadId(contact.leadId);
    try {
      await enrichedLeadService.markRejected(contact.leadId, reason);
      updateContactStatus(contact.leadId, { status: 'REJECTED', lostReason: reason });
      const remainingActive = contacts.filter(
        (c) => c.leadId !== contact.leadId && c.status !== 'REJECTED',
      );
      if (onLeadUpdated) {
        onLeadUpdated({
          leadId: lead.id,
          processId: lead.processId || lead.id,
          action: remainingActive.length === 0 ? 'REJECTED' : 'CONTACT_REJECTED',
        });
      }
      if (remainingActive.length === 0) onClose();
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleCardStatusAction(action) {
    if (updatingLeadId) return;

    if (action === 'CONVERTED') {
      if (!confirm('Marcar este lead como convertido?')) return;
    } else if (action === 'LOST') {
      const reason = window.prompt('Motivo da perda:', lead.lostReason || '');
      if (reason === null) return;
      setUpdatingLeadId('card');
      try {
        await enrichedLeadService.applyStatusToCard(lead, 'LOST', reason);
        setContacts((prev) =>
          prev.map((c) => ({ ...c, status: 'LOST', lostReason: reason })),
        );
        if (onLeadUpdated) {
          onLeadUpdated({
            leadId: lead.id,
            processId: lead.processId || lead.id,
            action: 'LOST',
          });
        }
      } catch (err) {
        alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
      } finally {
        setUpdatingLeadId(null);
      }
      return;
    } else if (action === 'REJECTED') {
      const reason = window.prompt('Motivo da inaptidão (opcional):', lead.lostReason || '');
      if (reason === null) return;
      setUpdatingLeadId('card');
      try {
        await enrichedLeadService.applyStatusToCard(lead, 'REJECTED', reason);
        setContacts((prev) =>
          prev.map((c) => ({ ...c, status: 'REJECTED', lostReason: reason })),
        );
        if (onLeadUpdated) {
          onLeadUpdated({
            leadId: lead.id,
            processId: lead.processId || lead.id,
            action: 'REJECTED',
          });
        }
        onClose();
      } catch (err) {
        alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
      } finally {
        setUpdatingLeadId(null);
      }
      return;
    }

    setUpdatingLeadId('card');
    try {
      await enrichedLeadService.applyStatusToCard(lead, action);
      setContacts((prev) => prev.map((c) => ({ ...c, status: action })));
      if (onLeadUpdated) {
        onLeadUpdated({
          leadId: lead.id,
          processId: lead.processId || lead.id,
          action,
        });
      }
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
    } finally {
      setUpdatingLeadId(null);
    }
  }

  async function handleReopen(contact) {
    const inContact =
      contact.status === 'CONTACTED' || Boolean(contact.assignedTo);
    const label = inContact
      ? `Remover o status "Em contato"${contact.assignedTo ? ` (${contact.assignedTo})` : ''} e liberar este lead?`
      : `Reabrir ${contact.name} como novo lead?`;
    if (!confirm(label)) return;

    setUpdatingLeadId(contact.leadId);
    try {
      await enrichedLeadService.reopenContact(contact.leadId);
      updateContactStatus(contact.leadId, { status: 'NEW', assignedTo: undefined, lostReason: undefined });
      if (onLeadUpdated) onLeadUpdated();
    } catch (err) {
      alert(`Não foi possível atualizar o status: ${err?.message || 'erro'}`);
    } finally {
      setUpdatingLeadId(null);
    }
  }

  if (!lead) return null;

  const tabButton = (id, icon, label, badge) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center space-x-2 ${
        activeTab === id
          ? 'bg-white text-blue-700 shadow-sm'
          : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:bg-opacity-50'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
      {badge}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-5 border-b border-gray-200">
          <div className="flex justify-between items-start mb-4 gap-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2 flex-wrap gap-y-2">
                <h3 className="text-2xl font-bold text-gray-900">{authorName}</h3>
                {companyName && (
                  <span className="px-3 py-1 text-xs font-semibold rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                    {companyName}
                  </span>
                )}
                <CreditProductBadge tipoProduto={processData?.tipoProduto} />
                {loaLabel && (
                  <span
                    className="px-3 py-1 text-xs font-semibold rounded-md bg-slate-100 text-slate-800 border border-slate-200"
                    title="Ano estimado da Lei Orçamentária Anual"
                  >
                    {loaLabel}
                  </span>
                )}
                {(() => {
                  const meta = enrichedLeadService.getStatusMeta(lead.status);
                  if (!meta) return null;
                  const label =
                    meta.key === 'CONTACTED' && lead.assignedTo
                      ? `Em contato • ${lead.assignedTo}`
                      : meta.label;
                  return (
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${meta.className}`}>
                      {label}
                    </span>
                  );
                })()}
              </div>

              {document && <p className="text-gray-600">CPF: {document}</p>}

              {processData?.numeroProcesso && (
                <p className="text-sm text-gray-600 font-mono mt-1">
                  Processo: {enrichedLeadService.formatProcessNumber(processData.numeroProcesso)}
                </p>
              )}
            </div>

            <div className="flex items-start gap-2 shrink-0">
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {lead.status !== 'CONVERTED' && (
                  <button
                    type="button"
                    onClick={() => handleCardStatusAction('CONVERTED')}
                    disabled={!!updatingLeadId}
                    className={leadStatusActionClass.converted}
                    title="Marcar como convertido"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Convertido
                  </button>
                )}
                {lead.status !== 'LOST' && (
                  <button
                    type="button"
                    onClick={() => handleCardStatusAction('LOST')}
                    disabled={!!updatingLeadId}
                    className={leadStatusActionClass.lost}
                    title="Marcar como perdido"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Perdido
                  </button>
                )}
                {lead.status !== 'REJECTED' && (
                  <button
                    type="button"
                    onClick={() => handleCardStatusAction('REJECTED')}
                    disabled={!!updatingLeadId}
                    className={leadStatusActionClass.rejected}
                    title="Marcar como inapto"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    Inapto
                  </button>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-white hover:bg-opacity-50 rounded-lg p-2 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex space-x-1 bg-white bg-opacity-50 rounded-lg p-1">
            {tabButton('processo', '📋', 'Dados do Processo')}
            {hasParecer && tabButton('parecer', '📝', 'Parecer')}
            {tabButton(
              'contatos',
              '📞',
              'Contatos',
              contacts.length > 0 && (
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                  {contacts.length}
                </span>
              ),
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'processo' && processData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Número do Processo</p>
                  <p className="font-mono text-sm font-medium text-gray-900">
                    {enrichedLeadService.formatProcessNumber(processData.numeroProcesso)}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Tribunal</p>
                  <p className="text-sm font-medium text-gray-900">{processData.tribunal || 'N/A'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Vara</p>
                  <p className="text-sm font-medium text-gray-900">{processData.vara || 'N/A'}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Tipo de Justiça</p>
                  <p className="text-sm font-medium text-gray-900">
                    {processData.justiceTypeName || enrichedLeadService.getJusticaLabel(processData.esfera)}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Estado/Província</p>
                  <p className="text-sm font-medium text-gray-900">
                    {processData.provinceName || processData.estado || 'N/A'}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Cidade</p>
                  <p className="text-sm font-medium text-gray-900">{processData.cityName || 'N/A'}</p>
                </div>
              </div>

              {/* Cálculos e valores em destaque */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 mr-2">Cálculos e valores</p>
                  {processData?.score !== undefined && (
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      processData.score >= 70 ? 'bg-green-100 text-green-800' :
                      processData.score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      Score: {processData.score}
                    </span>
                  )}
                  {viabilidadeMeta && (
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${viabilidadeMeta.className}`}>
                      Viabilidade {viabilidadeMeta.label}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Valor Estimado</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {processData.valorEstimado > 0
                        ? enrichedLeadService.formatCurrency(processData.valorEstimado)
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {(processData.positiveSignals?.length > 0 || processData.blockers?.length > 0 || processData.warnings?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="font-semibold text-green-800 mb-3">Sinais positivos</p>
                    {processData.positiveSignals?.length > 0 ? (
                      <div className="space-y-2">
                        {processData.positiveSignals.map((signal, idx) => (
                          <p key={idx} className="text-sm text-green-900">• {signal}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-green-700">Nenhum sinal positivo consolidado.</p>
                    )}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="font-semibold text-red-800 mb-3">Impedimentos</p>
                    {processData.blockers?.length > 0 ? (
                      <div className="space-y-2">
                        {processData.blockers.map((blocker, idx) => (
                          <p key={idx} className="text-sm text-red-900">• {blocker}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-red-700">Nenhum impeditivo relevante identificado.</p>
                    )}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="font-semibold text-yellow-800 mb-3">Alertas</p>
                    {processData.warnings?.length > 0 ? (
                      <div className="space-y-2">
                        {processData.warnings.map((warning, idx) => (
                          <p key={idx} className="text-sm text-yellow-900">• {warning}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-yellow-700">Sem alertas relevantes.</p>
                    )}
                  </div>
                </div>
              )}

              {checklistEntries.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Checklist jurídico-comercial</p>
                  <div className="space-y-3">
                    {checklistEntries.map((item, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                          <p className="font-medium text-gray-900">{item.label || 'Pergunta'}</p>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            {formatNormalizedAnswer(item.normalizedAnswer)}
                            {item.inferred ? ' • inferido' : ''}
                          </span>
                        </div>
                        {item.answer && (
                          <p className="text-sm text-gray-700 leading-relaxed">{item.answer}</p>
                        )}
                        {item.inferenceReason && (
                          <p className="text-xs text-gray-500 mt-2">{item.inferenceReason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Autor primeiro, advogado depois */}
              {processData.poloAtivo && processData.poloAtivo.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Polo Ativo (Autor):</p>
                  <div className="space-y-2">
                    {processData.poloAtivo.map((parte, idx) => (
                      <div key={idx} className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <p className="font-medium text-gray-900">{parte.nome}</p>
                        <p className="text-sm text-gray-600">{parte.tipo}</p>
                        {parte.documentos && parte.documentos.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {parte.documentos.map((doc, docIdx) => (
                              <p key={docIdx} className="text-xs text-gray-600">
                                {doc.type}: {doc.value}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {processData.poloPassivo && processData.poloPassivo.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Polo Passivo (Réu):</p>
                  <div className="space-y-2">
                    {processData.poloPassivo.map((parte, idx) => (
                      <div key={idx} className="bg-red-50 border border-red-200 p-4 rounded-lg">
                        <p className="font-medium text-gray-900">{parte.nome}</p>
                        <p className="text-sm text-gray-600">{parte.tipo}</p>
                        {parte.documentos && parte.documentos.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {parte.documentos.map((doc, docIdx) => (
                              <p key={docIdx} className="text-xs text-gray-600">
                                {doc.type}: {doc.value}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {processData.advogados && processData.advogados.length > 0 && (
                <div>
                  <p className="font-semibold text-gray-900 mb-3">Advogados:</p>
                  <ul className="space-y-2">
                    {processData.advogados.map((adv, idx) => (
                      <li key={idx} className="bg-purple-50 border border-purple-200 px-4 py-2 rounded-lg text-sm text-gray-900">
                        {adv}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'parecer' && (
            <div className="space-y-4">
              {parecerText ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <MemorandoContent text={decodeHtmlEntities(String(parecerText))} />
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500">Nenhum parecer disponível.</p>
                </div>
              )}

              {questionnaireText && (
                <details className="bg-white border border-gray-200 rounded-lg group">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Parecer (Questionário)
                    </span>
                    <svg className="w-4 h-4 text-gray-500 flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {decodeHtmlEntities(String(questionnaireText))}
                    </p>
                  </div>
                </details>
              )}
            </div>
          )}

          {activeTab === 'contatos' && (
            <div className="space-y-4">
              {contacts.map((contact, idx) => {
                const statusMeta = enrichedLeadService.getStatusMeta(contact.status);
                const busy = enrichedLeadService.isContactBusy(contact.status, contact.assignedTo);
                const isUpdating = updatingLeadId === contact.leadId;
                const phones = contact.phones || [];
                const emails = contact.emails || [];
                const isLawyer = isLawyerContact(contact);
                const matchedPartner = isLawyer ? getMatchedPartnerForContact(contact) : null;
                const contactBlocked = isLeadContactBlocked(contact, matchedPartner);
                const blockedByBadPartner = isBadLawyerPartner(matchedPartner);

                return (
                  <div
                    key={contact.leadId || idx}
                    className={`border rounded-xl overflow-hidden ${
                      contactBlocked ? 'border-red-300' : 'border-gray-200'
                    }`}
                  >
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-900">{contact.name}</p>
                        <p className="text-xs text-gray-500">
                          {contact.tipo || 'Parte'}
                          {contact.document ? ` • ${contact.document}` : ''}
                        </p>
                        {contactBlocked && (
                          <p className="text-xs text-red-700 mt-0.5 font-medium">
                            {blockedByBadPartner
                              ? `Parceiro ruim — contato desabilitado`
                              : 'Contato bloqueado — WhatsApp, e-mail e agente desabilitados'}
                          </p>
                        )}
                        {busy && contact.assignedTo && (
                          <p className="text-xs text-blue-700 mt-0.5">
                            Em contato por {contact.assignedTo}
                          </p>
                        )}
                        {contact.status === 'LOST' && contact.lostReason && (
                          <p className="text-xs text-red-700 mt-0.5">
                            Motivo da perda: {contact.lostReason}
                          </p>
                        )}
                        {contact.status === 'REJECTED' && contact.lostReason && (
                          <p className="text-xs text-slate-700 mt-0.5">
                            Motivo da inaptidão: {contact.lostReason}
                          </p>
                        )}
                        {matchedPartner && (
                          <div className="mt-2">
                            <PartnerLawyerBadge partner={matchedPartner} compact />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        {contactBlocked && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border bg-red-100 text-red-800 border-red-200">
                            <ShieldOff className="w-3.5 h-3.5" />
                            {blockedByBadPartner ? 'Parceiro ruim' : 'Contato bloqueado'}
                          </span>
                        )}
                        {statusMeta && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold border ${statusMeta.className}`}>
                            {statusMeta.key === 'CONTACTED' && contact.assignedTo
                              ? `Em contato • ${contact.assignedTo}`
                              : statusMeta.label}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-4 space-y-4">
                      {/* Ações de status */}
                      <div className="flex flex-wrap gap-2">
                        {!busy && (
                          <button
                            onClick={() => handleMarkInContact(contact)}
                            disabled={isUpdating || contactBlocked}
                            title={contactBlocked ? 'Contato bloqueado' : undefined}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            Marcar em contato
                          </button>
                        )}
                        {contact.status !== 'CONVERTED' && (
                          <button
                            onClick={() => handleMarkConverted(contact)}
                            disabled={isUpdating}
                            className={leadStatusActionClass.converted}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Convertido
                          </button>
                        )}
                        {contact.status !== 'LOST' && (
                          <button
                            onClick={() => handleMarkLost(contact)}
                            disabled={isUpdating}
                            className={leadStatusActionClass.lost}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Perdida
                          </button>
                        )}
                        {contact.status !== 'REJECTED' && (
                          <button
                            onClick={() => handleMarkRejected(contact)}
                            disabled={isUpdating}
                            className={leadStatusActionClass.rejected}
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Inapto
                          </button>
                        )}
                        {(contact.status && contact.status !== 'NEW') && (
                          <button
                            onClick={() => handleReopen(contact)}
                            disabled={isUpdating}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-medium rounded-md transition"
                            title={
                              contact.status === 'CONTACTED' || contact.assignedTo
                                ? 'Remove o status Em contato e libera o lead'
                                : 'Reabre o lead como Novo'
                            }
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            {contact.status === 'CONTACTED' ||
                            (contact.assignedTo &&
                              ['CONTACTED', 'PROPOSAL_SENT', 'NEGOTIATING'].includes(
                                contact.status,
                              ))
                              ? 'Remover em contato'
                              : 'Reabrir'}
                          </button>
                        )}
                        {isLawyer && !matchedPartner && (
                          <button
                            type="button"
                            onClick={() => handleOpenPartnerModal(contact)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-md transition"
                            title="Cadastrar este advogado como parceiro ECP"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Adicionar como parceiro
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleContactBlocked(contact)}
                          disabled={isUpdating || !contact.leadId || blockedByBadPartner}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed ${
                            contactBlocked
                              ? 'border border-red-300 bg-red-50 hover:bg-red-100 text-red-800'
                              : 'border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900'
                          }`}
                          title={
                            blockedByBadPartner
                              ? 'Bloqueio automático por parceiro ruim — altere a classificação em Advogados Parceiros'
                              : contact.contactBlocked
                                ? 'Desbloquear canais de contato'
                                : 'Bloquear WhatsApp, e-mail e agente'
                          }
                        >
                          <ShieldOff className="w-3.5 h-3.5" />
                          {blockedByBadPartner
                            ? 'Parceiro ruim'
                            : contact.contactBlocked
                              ? 'Desbloquear contato'
                              : 'Bloquear contato'}
                        </button>
                      </div>

                      {/* Telefones */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Telefones</p>
                        {phones.length > 0 ? (
                          <div className="space-y-2">
                            {phones.map((phone, pIdx) => (
                              <div
                                key={pIdx}
                                className="flex items-center justify-between bg-gray-50 border border-gray-200 p-3 rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{phone.telefoneFormatado}</p>
                                  <p className="text-xs text-gray-600">
                                    {phone.tipoTelefone} - {phone.operadora}
                                    {phone.qualificacao === 1 && (
                                      <span className="ml-2 text-green-600 font-medium">✓ Verificado</span>
                                    )}
                                  </p>
                                </div>
                                {contactBlocked ? (
                                  <button
                                    type="button"
                                    disabled
                                    title="Contato bloqueado"
                                    className="flex items-center space-x-2 px-3 py-2 bg-gray-300 text-gray-600 rounded-lg text-sm font-medium cursor-not-allowed"
                                  >
                                    <ShieldOff className="w-4 h-4" />
                                    <span>Bloqueado</span>
                                  </button>
                                ) : (
                                  <a
                                    href={phone.whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleWhatsAppClick(contact)}
                                    className="flex items-center space-x-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                                  >
                                    <WhatsAppIcon className="w-4 h-4" />
                                    <span>WhatsApp</span>
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">Nenhum telefone disponível</p>
                        )}
                      </div>

                      {/* E-mails */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">E-mails</p>
                        {emails.length > 0 ? (
                          <div className="space-y-2">
                            {emails.map((email, eIdx) => (
                              <div
                                key={eIdx}
                                className="flex items-center justify-between bg-gray-50 border border-gray-200 p-3 rounded-lg"
                              >
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{email.email}</p>
                                  {email.qualificacao === 1 && (
                                    <span className="text-xs text-green-600 font-medium">✓ Verificado</span>
                                  )}
                                </div>
                                {contactBlocked ? (
                                  <button
                                    type="button"
                                    disabled
                                    title="Contato bloqueado"
                                    className="px-3 py-2 bg-gray-300 text-gray-600 rounded-lg text-sm font-medium cursor-not-allowed"
                                  >
                                    Bloqueado
                                  </button>
                                ) : (
                                  <a
                                    href={`mailto:${email.email}`}
                                    className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                                  >
                                    E-mail
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">Nenhum e-mail disponível</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>

      <QuickLawyerPartnerModal
        open={partnerModalOpen}
        onClose={() => {
          setPartnerModalOpen(false);
          setPartnerCandidate(null);
        }}
        sourceLabel={`lead ${processData?.numeroProcesso || lead?.name || lead?.id || ''}`}
        fonteCadastro="LEAD"
        initialLawyers={partnerCandidate ? [partnerCandidate] : []}
        onCreated={handlePartnerCreated}
      />
    </div>
  );
}